<template>
    <div>
        <b-modal
            v-model="modalShow"
            size="lg"
            title="マイクとカメラのテスト"
            @hidden="close">
            <div class="modal-body">
                <div class="container">
                    <template v-if="errorType === null">
                        <h5 class="device-name">マイク</h5>
                        <div class="devicesWrapper">
                            <template v-if="selectedAudioId !== null">
                                <div class="deviceContainer mt-1">
                                    <b-icon-mic/>
                                    <select
                                        v-model="selectedAudioId"
                                        class="custom-select small">
                                        <option
                                            v-for="audio in audios"
                                            :value="audio.deviceId"
                                            :key="audio.deviceId">
                                            {{ audio.label }}
                                        </option>
                                    </select>
                                </div>
                                <div class="progress mt-2">
                                    <div
                                        id="progress-bar-test"
                                        class="bg-success"
                                        role="progressbar"
                                        aria-valuenow="0"
                                        aria-valuemin="0"
                                        aria-valuemax="100">
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="deviceLoadingWrapper d-flex justify-content-start align-items-center">
                                    <b-spinner variant="yokumiru-user"/>
                                    <div class="text-muted ml-2">マイクの接続を確認中です…</div>
                                </div>
                            </template>
                        </div>

                        <h5 class="device-name mt-3">カメラ</h5>
                        <div class="devicesWrapper">
                            <template v-if="selectedVideoId !== null">
                                <div class="deviceContainer">
                                    <b-icon-camera-video-fill/>
                                    <select
                                        v-model="selectedVideoId"
                                        class="custom-select small">
                                        <option
                                            v-for="video in videos"
                                            :value="video.deviceId"
                                            :key="video.deviceId">
                                            {{ video.label }}
                                        </option>
                                    </select>
                                </div>
                            </template>
                            <template v-else>
                                <div class="deviceLoadingWrapper d-flex justify-content-start align-items-center">
                                    <b-spinner variant="yokumiru-user"/>
                                    <div class="text-muted ml-2">カメラの接続を確認中です…</div>
                                </div>
                            </template>
                        </div>
                        <div class="videoContainer mt-2">
                            <div class="videoContainer__inner">
                                <div id="local-video-test"></div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="testResult d-flex justify-content-center align-items-center">
                            <div v-if="errorType === 'client.join'">
                                <h6><b-icon-exclamation-triangle-fill/>&nbsp;通信が開始できませんでした。</h6>
                                <p>お使いの環境ではビデオ通話を利用できないようです。</p>
                                <ol>
                                    <li>セキュリティソフトやポート制限により、通信がブロックされている可能性があります。</li>
                                    <li>上手くいかない場合、他のインターネット回線や別のデバイスからもテストをお試しください。</li>
                                </ol>
                            </div>
                            <div v-else-if="errorType === 'permission'">
                                <h6><b-icon-exclamation-triangle-fill/>&nbsp;マイクとカメラへのアクセスがブロックされています</h6>
                                <ol class="mb-0">
                                    <li>アドレスバー右端の<img src="/images/common/test_error_permission.png">アイコンをクリックして、マイクとカメラへのアクセスを許可します。</li>
                                    <li>一旦画面を閉じた後、再度マイクとカメラのテストをお試しください。</li>
                                </ol>
                            </div>
                            <div v-else-if="errorType === 'unknown'">
                                <h6><b-icon-exclamation-triangle-fill/>&nbsp;マイクとカメラの接続を確認できませんでした</h6>
                                <p>お使いの環境ではマイクとカメラを利用できないようです。</p>
                                <ol>
                                    <li>マイクとカメラへのアクセス許可設定を変更した場合、ブラウザをリロードしてください。</li>
                                    <li>上手くいかない場合、他のインターネット回線や別のデバイスからもテストをお試しください。</li>
                                </ol>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div slot="modal-footer">
                <div class="d-flex justify-content-end align-items-center">
                    <b-button
                        variant="yokumiru-gray"
                        class="ml-2 mr-2"
                        @click="close">
                        {{ isEnteringRoom === true ? 'キャンセル' : '閉じる' }}
                    </b-button>
                    <b-button
                        v-if="isEnteringRoom === true"
                        variant="yokumiru-doctor"
                        class="ml-2 mr-2"
                        @click="enterRoom">
                        入室
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import AgoraRTC from 'agora-rtc-sdk-ng'
import _ from 'lodash'
import constants from "../../../constants"

export default {
    name: 'VideoTestModal',

    props: {
        userType: {
            type: String,
            default: 'user',
        },
        consultation: {
            type: Object,
            required: true,
        },
        roomKey: {
            type: String,
            required: true,
        },
        apiKey: {
            type: String,
            required: true,
        },
        token: {
            type: String,
            required: true,
        },
        // @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/clientconfig.html#mode
        transcode: {
            type: String,
            default: 'rtc',
        },
        debug: {
            type: String,
            default: '0',
        },
    },

    data: function () {
        return {
            modalShow: false,
            isEnteringRoom: false,
            client: null,
            localVideo: null,
            localAudio: null,
            timer: {
                clientJoin: null,
                localDevice: null,
                enterRoom: null,
            },
            videos: [],
            audios: [],
            selectedVideoId: null,
            selectedAudioId: null,
            localVideoState: false,
            localAudioState: false,
            inProgress: {
                switchLocalVideo: false,
                switchLocalAudio: false,
                enterRoom: false,
            },
            agoraInitState: {
                errorHandled: false,
            },
            volumeAnimation: null,
            errorType: null,
        }
    },

    computed: {
        /**
         * agoraセッションにおけるユニークID
         * 先頭の数字でマイクとカメラのテストと見なす（他のイベントリスナで検知させない）
         *
         * @see NetworkTestModal.vue
         * @return {number}
         */
        uid: function () {
            if (this.userType === 'user') {
                // 会員モード
                const id = '7' + this.consultation.id.toString()
                return Number(id)
            }
            else {
                // 医師モード
                const id = '9' + this.consultation.id.toString()
                return Number(id)
            }
        },

        /**
         * デバッグモード判定
         *
         * @return {boolean}
         */
        isDebug: function () {
            return this.debug === '1'
        },

        /**
         * CloudWatch Logs 出力時のユーザタイプフォーマット
         *
         * @return {string}
         */
        cwLogsUserType: function () {
            return this.userType === 'user' ? 'user:web (video-test)' : `${this.userType} (video-test)`
        },
    },

    watch: {
        selectedVideoId: function () {
            this.switchVideo()
        },

        selectedAudioId: function () {
            this.switchAudio()
        },

        errorType: function (val) {
            if (val !== null) {
                this.clearAllMediaAndLeave()
            }
        },
    },

    created: function () {
        bus.$on('open-modal-video-test', this.open)
    },

    beforeDestroy: function () {
        bus.$off('open-modal-video-test')
    },

    methods: {
        /**
         * モーダル表示
         *
         * @param {boolean} isEnteringRoom
         */
        open: function (isEnteringRoom = false) {
            this.modalShow = true
            this.isEnteringRoom = isEnteringRoom
            this.initAgora()
        },

        /**
         * モーダル非表示
         */
        close: function () {
            this.clearAllMediaAndLeave()
            this.clearTestState()
            this.modalShow = false
        },

        /**
         * CloudWatch Logs キュー追加
         *
         * @param {string} message
         */
        queueCwLogs: function (message) {
            bus.$emit('queue-cloud-watch-logs-user', message)
        },

        /**
         * 入室処理
         */
        enterRoom: function () {
            if (this.inProgress.enterRoom) return
            this.inProgress.enterRoom = true

            this.close()
            this.timer.enterRoom = setInterval(() => {
                if (this.client === null) {
                    this.inProgress.enterRoom = false
                    bus.$emit('user-enter-room')
                    clearInterval(this.timer.enterRoom)
                }
            }, 300)
        },

        /**
         * 初期化処理
         */
        initAgora: async function () {
            if (this.client !== null) return

            if (!AgoraRTC.checkSystemRequirements()) {
                this.queueCwLogs(`[${this.cwLogsUserType}] AgoraRTC.checkSystemRequirements(): System requirements not met.`)
            }

            AgoraRTC.setLogLevel(4)

            /**
             * @type {IAgoraRTCClient}
             * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/iagorartc.html#createclient
             * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/clientconfig.html
             */
            this.client = AgoraRTC.createClient({ mode: this.transcode, codec: 'vp8' })

            // イベントリスナ追加
            this.subscribeStreamEvents()

            // client.joinのタイムアウト用
            this.timer.clientJoin = setTimeout(() => this.errorType = 'client.join', 10000)

            /**
             * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/iagorartcclient.html#join
             */
            await this.client.join(this.apiKey, this.roomKey, this.token, this.uid)
                .then((uid) => {
                    clearTimeout(this.timer.clientJoin)
                    this.queueCwLogs(`[${this.cwLogsUserType}] client join success. uid:${uid}`)
                    this.timer.localDevice = setInterval(() => this.updateLocalDevices(), 2000)
                })
                .catch((e) => {
                    clearTimeout(this.timer.clientJoin)
                    this.agoraInitState.errorHandled = true
                    this.queueCwLogs(`[${this.cwLogsUserType}] client join failed. ${e}`)
                })

            // client.join 後すぐに取得だと取れないケースがあるためディレイをかける
            setTimeout(() => this.logLocalTracks(), 5000)
            if (this.client) {
                this.queueCwLogs(`[${this.cwLogsUserType}] agora channelName:${this.client.channelName} uid:${this.client.uid} connectionState:${this.client.connectionState}`)
            }
        },

        /**
         * Agoraのイベントリスナ追加
         */
        subscribeStreamEvents: function () {
            // リモートビデオが存在しないためリスナ追加無し
        },

        /**
         * ビデオの切り替え
         * @see https://docs.agora.io/en/Video/test_switch_device_web_ng?platform=Web
         */
        switchVideo: async function () {
            if (this.selectedVideoId === null || this.inProgress.switchLocalVideo) return
            this.inProgress.switchLocalVideo = true

            if (this.localVideo === null) {
                await this.initLocalVideo()
            }
            else {
                const currentLabel = this.localVideo.getTrackLabel()
                const selectedVideo = this.videos.find((video) => {
                    return video.deviceId === this.selectedVideoId
                })

                // 現在のビデオデバイスと異なるものが選択された場合のみ切り替え
                if (selectedVideo && selectedVideo.label !== currentLabel) {
                    this.localVideo.setDevice(this.selectedVideoId)
                        .then(() => {
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local video success. label:${selectedVideo.label} deviceId:${selectedVideo.deviceId} groupId:${selectedVideo.groupId}`)
                        })
                        .catch((e) => {
                            this.errorType = 'unknown'
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local video failed. ${e}`)
                        })
                }
            }

            this.inProgress.switchLocalVideo = false
        },

        /**
         * オーディオの切り替え
         * @see https://docs.agora.io/en/Video/test_switch_device_web_ng?platform=Web
         */
        switchAudio: async function () {
            if (this.selectedAudioId === null || this.inProgress.switchLocalAudio) return
            this.inProgress.switchLocalAudio = true

            if (this.localAudio === null) {
                await this.initLocalAudio()
            }
            else {
                const currentLabel = this.localAudio.getTrackLabel()
                const selectedAudio = this.audios.find((audio) => {
                    return audio.deviceId === this.selectedAudioId
                })

                // 現在のオーディオデバイスと異なるものが選択された場合のみ切り替え
                if (selectedAudio && selectedAudio.label !== currentLabel) {
                    this.localAudio.setDevice(this.selectedAudioId)
                        .then(() => {
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local audio success. label:${selectedAudio.label} deviceId:${selectedAudio.deviceId} groupId:${selectedAudio.groupId}`)
                        })
                        .catch((e) => {
                            this.errorType = 'unknown'
                            this.queueCwLogs(`[${this.cwLogsUserType}] switch local audio failed. ${e}`)
                        })
                }
            }

            this.inProgress.switchLocalAudio = false
        },

        /**
         * LocalVideoを初期化
         */
        initLocalVideo: async function () {
            if (this.selectedVideoId) {
                try {
                    this.localVideo = await AgoraRTC.createCameraVideoTrack({cameraId: this.selectedVideoId})
                    this.localVideo.play('local-video-test')
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local video success.`)
                }
                catch (e) {
                    this.agoraInitState.errorHandled = true
                    this.errorType = 'permission'
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local video failed. ${e}`)
                    return
                }
            }

            try {
                await this.client.publish(this.localVideo)
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local video success.`)
            }
            catch (e) {
                this.agoraInitState.errorHandled = true
                this.errorType = 'unknown'
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local video failed. ${e}`)
            }
        },

        /**
         * LocalAudioを初期化
         */
        initLocalAudio: async function () {
            if (this.selectedAudioId) {
                try {
                    this.localAudio = await AgoraRTC.createMicrophoneAudioTrack({microphoneId: this.selectedAudioId})
                    this.volumeAnimation = requestAnimationFrame(this.setVolumeWave)
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local audio success.`)
                }
                catch (e) {
                    this.agoraInitState.errorHandled = true
                    this.errorType = 'permission'
                    this.queueCwLogs(`[${this.cwLogsUserType}] create local audio failed. ${e}`)
                    return
                }
            }

            try {
                await this.client.publish(this.localAudio)
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local audio success.`)
            }
            catch (e) {
                this.agoraInitState.errorHandled = true
                this.errorType = 'unknown'
                this.queueCwLogs(`[${this.cwLogsUserType}] publish local audio failed. ${e}`)
            }
        },

        /**
         * テスト状態のクリア
         */
        clearTestState: function () {
            // client.joinタイムアウトをクリア
            clearTimeout(this.timer.clientJoin)

            // エラー状態を解除
            this.errorType = null
        },

        /**
         * すべてのメディアをクリアしてチャンネルから退室
         */
        clearAllMediaAndLeave: function () {
            clearInterval(this.timer.localDevice)
            cancelAnimationFrame(this.volumeAnimation)
            this.clearLocalMedia()

            // マイクとカメラの許可がされていない場合、clientがnullのままになるケースがあるためif判定
            if (this.client) {
                this.client.leave()
            }

            this.videos = []
            this.audios = []
            this.selectedVideoId = null
            this.selectedAudioId = null
            this.localVideoState = false
            this.localAudioState = false

            this.client = null
        },

        /**
         * メディアをクリア
         */
        clearLocalMedia: function () {
            if (this.localVideo !== null) {
                this.localVideo.close()
                if (this.client) this.client.unpublish(this.localVideo)
                this.localVideo = null
            }

            if (this.localAudio !== null) {
                this.localAudio.close()
                if (this.client) this.client.unpublish(this.localAudio)
                this.localAudio = null
            }
        },

        /**
         * デバイスを監視
         * デバイスの追加・解除が発生した場合は接続状態更新する
         *
         * @see https://developer.mozilla.org/ja/docs/Web/API/MediaDevices/enumerateDevices
         */
        updateLocalDevices: function () {
            AgoraRTC.getCameras().then((devices) => {
                this.updateLocalVideos(devices)
            })
            .catch((e) => {
                this.handleErrorUpdateLocalDevices(e)
            })

            AgoraRTC.getMicrophones().then((devices) => {
                this.updateLocalAudios(devices)
            })
            .catch((e) => {
                this.handleErrorUpdateLocalDevices(e)
            })
        },

        handleErrorUpdateLocalDevices: function (e) {
            this.queueCwLogs(`[${this.cwLogsUserType}] update local devices failed. ${e}`)

            // @see AgoraRTCError
            if (e.hasOwnProperty('code')) {
                if (e.code === 'PERMISSION_DENIED') {
                    this.errorType = 'permission'
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalDevices: AgoraRTCError PERMISSION_DENIED`)
                    return
                }
            }
            this.errorType = 'unknown'
            this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalDevices: Error UNKNOWN ${e}`)
        },

        /**
         * カメラ接続状態を更新
         *
         * @param {object} videos
         */
        updateLocalVideos: function (videos) {
            const prev = this.videos.map(video => video.deviceId)
            const current = videos.map(video => video.deviceId)
            this.videos = videos

            if (prev.length !== current.length) {
                // カメラ未接続
                if (current.length < 1) {
                    this.selectedVideoId = null
                    this.localVideoState = false
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: all local videos disconnected.`)
                }
                // カメラ未接続状態からカメラを接続
                else if (prev.length < 1) {
                    this.selectedVideoId = this.autoSelectDevice(this.videos)
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: new local video connected.`)
                }
                // 複数台カメラが接続された状態でカメラを追加
                else if (current.length > prev.length) {
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: additional new local video connected.`)
                }
                // 複数台カメラが接続された状態でカメラを解除
                else if (prev.length > current.length) {
                    const currentVideo = this.videos.find(video => {
                        return video.deviceId === this.selectedVideoId
                    })
                    if (!currentVideo) {
                        this.selectedVideoId = this.autoSelectDevice(this.videos)
                    }
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalVideos: part of local video disconnected.`)
                }

                this.logLocalTracks()
            }
            this.localVideoState = !!this.selectedVideoId
        },

        /**
         * マイク接続状態を更新
         *
         * @param {object} audios
         */
        updateLocalAudios: function (audios) {
            const prev = this.audios.map(audio => audio.deviceId)
            const current = audios.map(audio => audio.deviceId)
            this.audios = audios

            if (prev.length !== current.length) {
                // マイク未接続
                if (current.length < 1) {
                    this.selectedAudioId = null
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: all local audios disconnected.`)
                }
                // マイク未接続状態からマイクを接続
                else if (prev.length < 1) {
                    this.selectedAudioId = this.autoSelectDevice(this.audios)
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: new local audio connected.`)
                }
                // 複数台マイクが接続された状態でマイクを追加
                else if (current.length > prev.length) {
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: additional new local audio connected.`)
                }
                // 複数台マイクが接続された状態でマイクを解除
                else if (prev.length > current.length) {
                    const currentAudio = this.audios.find(audio => {
                        return audio.deviceId === this.selectedAudioId
                    })
                    if (!currentAudio) {
                        this.selectedAudioId = this.autoSelectDevice(this.audios)
                    }
                    this.queueCwLogs(`[${this.cwLogsUserType}] updateLocalAudios: part of local audio disconnected.`)
                }

                this.logLocalTracks()
            }
            this.localAudioState = !!this.selectedAudioId
        },

        /**
         * カメラ／マイクの自動切替
         *
         * @param {array} devices
         */
        autoSelectDevice: function (devices) {
            if (devices.length < 1) return null

            // システムデフォルトのデバイスを優先選択する（見つからない場合は先頭のデバイスを選択）
            const device = devices.find((device) => {
                return device.deviceId === 'default'
                    || device.label.includes('Built-in')
                    || device.label.includes('規定')
                    || device.label.includes('内蔵')
            }) || devices[0]

            if (device) {
                this.queueCwLogs(`[${this.cwLogsUserType}] autoSelectDevice label:${device.label} kind:${device.kind} deviceId:${device.deviceId} groupId:${device.groupId}`)
            }
            return device ? device.deviceId : null
        },

        /**
         * ローカルデバイスの状態をログ収集
         */
        logLocalTracks: function () {
            if (!this.client || !this.client.localTracks) {
                return
            }
            this.client.localTracks.forEach((track, index) => {
                this.queueCwLogs(`[${this.cwLogsUserType}] localTrackInfo[${index}] label:${track.getTrackLabel()} type:${track.trackMediaType} trackId:${track.getTrackId()} enabled:${track.enabled} isPlaying:${track.isPlaying}`)
            })
        },

        /**
         * マイク音量のプログレスバーアニメーション
         */
        setVolumeWave: function () {
            this.volumeAnimation = requestAnimationFrame(this.setVolumeWave)

            if (this.localAudio !== null) {
                const $progressBar = $('#progress-bar-test')
                $progressBar.css('width', this.localAudio.getVolumeLevel() * 100 + "%")
                $progressBar.attr('aria-valuenow', this.localAudio.getVolumeLevel() * 100)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.videoContainer {
    position: relative;
    width: 100%;

    &::before {
        content: '';
        display: block;
        padding-top: 75%;
    }

    .videoContainer__inner {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
    }

    #local-video-test {
        display: block;
        width: 100%;
        height: 100%;
    }
}

.devicesWrapper {
    margin-top: 10px;

    .deviceContainer {
        position: relative;

        div {
            width: 100%;
        }

        svg {
            position: absolute;
            left: 10px;
            top: 12px;
            fill: #5c5d5e;
        }

        select {
            padding-left: 32px;
            cursor: pointer;
        }
    }
}

.deviceLoadingWrapper {
    span.spinner-border {
        width: 20px;
        height: 20px;
    }
}

.testResult {
    h6 {
        font-size: 1rem;
        font-weight: bold;

        svg {
            fill: #F79E06;
        }
    }

    ol {
        padding-left: 1.2rem;

        li {
            img {
                width: 32px;
                margin: 0 4px;
            }
        }
    }
}
</style>
